import styled, { css } from 'styled-components';
import { Badge } from 'ui/badge';
import { Button } from 'ui/button';
import { Delete } from 'ui/icons/action/Delete';
import { body1Bold, body2, colors, smallShadow, spacing } from 'ui/theme';

// function here to simplify the control logic required to pull off a single-rule-only UI

export const StyledDeleteChildRuleContainer = styled.span`
  display: none;
`;

const DeleteIconMultiRuleStyles = `
  right: -12px;
  top: -12px;
`;
const DeleteIconSingleRuleStyles = `
  right: -27px;
  top: -27px;
`;

const ChildRuleBorderStyles = `
border: 2px solid ${colors.neutral300}; 
padding: 0 ${spacing.base} ${spacing.base};`;

// 1. only show a border if there are child rules
// 2. show the delete icon on hover at the top right of the border
const getSingleRuleContainerStyles = (hasChildRules: boolean) => `
  ${
    hasChildRules
      ? ChildRuleBorderStyles // border if child rules
      : `padding: 0;` // no border if no child rules
  }

  &:hover {
    ${StyledDeleteChildRuleContainer} {
      display: flex;
      position: absolute;
      ${DeleteIconSingleRuleStyles}
      width: 24px;
    }
  }
  `;

// once multiple rules are allowed, this may be the only style needed
const MultiRuleContainerStyles = `
background-color: ${colors.grey1};
padding: ${spacing.base};
`;

// we are temporarily supporting one-rule-only UI. if only one rule is allowed, adjust the container accordingly
export const StyledRuleBlockContainer = styled.ul`
  ${({
    isMaxOneRule = false,
    hasChildRules,
  }: { isMaxOneRule?: boolean; hasChildRules: boolean }) =>
    isMaxOneRule ? getSingleRuleContainerStyles(hasChildRules) : MultiRuleContainerStyles}
  border-radius: ${spacing.half};
  margin-top: ${spacing.base};
  margin-bottom: ${spacing.base};
`;

// if only one rule is allowed, adjust the padding accordingly
// TODO: remove ugly logic code when multiple rules are allowed
export const StyledChildRuleContainer = styled.div`
  ${({ isMaxOneRule = false }: { isMaxOneRule?: boolean }) =>
    isMaxOneRule ? `padding: 0;` : `padding: ${spacing.base};`}
  background-color: ${colors.white};
  border-radius: ${spacing.half};
  box-shadow: ${smallShadow};
  margin-top: ${spacing.base};
  position: relative;

  ${({ isMaxOneRule = false }: { isMaxOneRule: boolean }) =>
    !isMaxOneRule &&
    `
  &:hover {
    ${StyledDeleteChildRuleContainer} {
      display: flex;
      position: absolute;
     
      ${DeleteIconMultiRuleStyles}
      width: 24px;
      margin: ${spacing.half};
    }
  }`}
`;
export const StyledRuleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${body2};
  color: ${colors.secondaryText};
  margin-bottom: ${spacing.base};
`;

export const StyledRuleHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.quarter};
`;

export const StyledBoldRuleHeader = styled.span`
  ${body1Bold};
`;

export const StyledActionArea = styled.div<{ isMaxOneRule?: boolean }>`
  margin-top: ${spacing.base};
  ${body2};
  display: flex;
  flex-direction: row;
  align-items: center;

  ${({ isMaxOneRule }) =>
    isMaxOneRule &&
    css`
      margin-top: 0;
      background: ${colors.grey1};
      border-radius: ${spacing.half};
      border: ${spacing.eighth} solid transparent;
      padding: ${spacing.plusQuarter} ${spacing.base};
    `}
`;

export const StyledBadgeContainer = styled.div`
  margin: ${spacing.base} 0;
`;

export const StyledButton = styled(Button)`
  margin-right: ${spacing.threeQuarter};
`;

export const StyledBadge = styled(Badge)`
  margin-right: ${spacing.plusHalf};
`;

export const StyledDeleteChildRuleIcon = styled(Delete)`
  cursor: pointer;
  background-color: ${colors.red100};
  border-radius: 50%;
  padding: 2px;
`;
